/**
 * Auth state — unified for guests, staff, and owners.
 */

import { createSignal, createEffect } from 'solid-js'
import { requestOtp, verifyOtp, loginStaff, getGuestProfile } from './api'

export type UserRole = 'guest' | 'staff' | 'manager' | 'owner'

export interface UserVenue {
  venueId: string
  venueName: string
  role: UserRole
}

export interface User {
  id: string
  email: string
  balance: number
  itemsCheckedIn: number
  referralCode: string
  venues: UserVenue[]
}

const [token, setToken] = createSignal<string | null>(localStorage.getItem('session_token'))
const [user, setUser] = createSignal<User | null>(null)
const [activeVenue, setActiveVenue] = createSignal<UserVenue | null>(null)
const [mode, setMode] = createSignal<'guest' | 'staff'>('guest')
const [isLoading, setIsLoading] = createSignal(false)
const [error, setError] = createSignal<string | null>(null)

// Restore session on load
createEffect(async () => {
  if (token()) {
    try {
      const res = await getGuestProfile()
      if (res.data) {
        const u = res.data
        setUser({
          id: u.id,
          email: u.email,
          balance: u.balance,
          itemsCheckedIn: u.itemsCheckedIn,
          referralCode: u.referralCode,
          venues: u.roles || [],
        })
        // Auto-select first venue if user has staff roles
        if (u.roles?.length > 0) {
          setActiveVenue(u.roles[0])
        }
      } else {
        localStorage.removeItem('session_token')
        setToken(null)
      }
    } catch (err) {
      console.error('Auth restore failed:', err)
      localStorage.removeItem('session_token')
      setToken(null)
    }
  }
})

export function useAuth() {
  return {
    token,
    user,
    activeVenue,
    mode,
    isLoading,
    error,
    isAuthenticated: () => !!token() && !!user(),
    hasStaffRole: () => (user()?.venues?.length ?? 0) > 0,
    isOwner: () => activeVenue()?.role === 'owner',
    isManager: () => activeVenue()?.role === 'manager' || activeVenue()?.role === 'owner',
    isStaff: () => activeVenue()?.role === 'staff',

    async sendOtp(email: string): Promise<boolean> {
      setIsLoading(true)
      setError(null)
      const res = await requestOtp(email)
      if (res.error) { setError(res.error.message); setIsLoading(false); return false }
      setIsLoading(false)
      return true
    },

    async verifyOtp(email: string, code: string): Promise<boolean> {
      setIsLoading(true)
      setError(null)
      const res = await verifyOtp(email, code)
      if (res.error || !res.data) { setError(res.error?.message || 'Verification failed'); setIsLoading(false); return false }
      localStorage.setItem('session_token', res.data.token)
      setToken(res.data.token)
      await this.refreshProfile()
      setIsLoading(false)
      return true
    },

    async loginAsStaff(email: string, password: string): Promise<boolean> {
      setIsLoading(true)
      setError(null)
      const res = await loginStaff(email, password)
      if (res.error) { setError(res.error.message); setIsLoading(false); return false }
      if (res.data) {
        localStorage.setItem('session_token', res.data.token)
        setToken(res.data.token)
        setMode('staff')
        setActiveVenue({ venueId: res.data.staff.venueId, venueName: res.data.staff.venueName, role: res.data.staff.role as UserRole })
        await this.refreshProfile()
      }
      setIsLoading(false)
      return true
    },

    async refreshProfile() {
      const res = await getGuestProfile()
      if (res.data) {
        const u = res.data
        setUser({
          id: u.id, email: u.email, balance: u.balance,
          itemsCheckedIn: u.itemsCheckedIn, referralCode: u.referralCode,
          venues: u.roles || [],
        })
      }
    },

    switchVenue(venue: UserVenue) {
      setActiveVenue(venue)
    },

    switchMode(newMode: 'guest' | 'staff') {
      setMode(newMode)
    },

    logout() {
      localStorage.removeItem('session_token')
      setToken(null)
      setUser(null)
      setActiveVenue(null)
      setMode('guest')
    },
  }
}
